import {
  HeadingLarge,
  HeadingSmall,
  LabelLarge,
  Link,
  ParagraphLarge,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { useQueryClient } from "@tanstack/react-query"

import { selectTokenDecimals, useDappSelector } from "#/redux-state"
import { links, queryKeysFactory } from "#/shared/constants"
import {
  useEffectOnce,
  useMarketStoreProductAvailability,
  useMarketStoreProductDataById,
  useMarketStoreProductDetailsById,
  useOrderProduct,
} from "#/shared/hooks"
import { bigIntToCommaSeparatedFloat } from "#/shared/utils"
import { MarketItemDetailsLayoutWithActionButton } from "#/ui/Market/MarketItemDetails/components"
import MarketRedeemCodeSuccess from "#/ui/Market/MarketItemDetails/MarketRedeemCodeSuccess"

import MarketTag from "../MarketTag"

export default function MarketStoreProductDetails({
  productId,
}: {
  productId: string
}) {
  const [, theme] = useStyletron()
  const productData = useMarketStoreProductDataById(productId)

  const productDataDetails = useMarketStoreProductDetailsById(
    productData?.chainProductId,
  )

  const queryClient = useQueryClient()

  useEffectOnce(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeysFactory.mezoKeys.productDetails(),
      refetchType: "all",
    })
  })

  const {
    isFetching: isProductAvailabilityDataFetching,
    isInStock,
    isLimitPerCustomerReached,
    submitButtonText,
  } = useMarketStoreProductAvailability(productData?.chainProductId)

  const {
    orderProduct,
    isProcessing,
    isSuccess,
    hasEnoughFunds,
    hasError,
    errorMessage,
    result: { transactionHash, redeemCode },
  } = useOrderProduct(productData?.chainProductId)

  const musdDecimals = useDappSelector((state) =>
    selectTokenDecimals(state, "MUSD"),
  )

  if (!productData) return null

  if (isSuccess && transactionHash && redeemCode) {
    return (
      <MarketRedeemCodeSuccess
        type="store-product"
        transactionHash={transactionHash}
        redeemCode={redeemCode}
        image={productData.fullImage}
        successMessage={productData.successMessage}
        redeemButtonLabel="Redeem at Ledger.com"
        redeemLink={productData.productRedeemLink}
      />
    )
  }

  return (
    <MarketItemDetailsLayoutWithActionButton
      itemData={productData}
      submitButtonText={submitButtonText}
      submitCallback={orderProduct}
      submitButtonDataAttr={`${productData.id}-purchase`}
      isSubmitButtonDisabled={
        !productDataDetails ||
        !productData?.chainProductId ||
        !isInStock ||
        isLimitPerCustomerReached ||
        isProductAvailabilityDataFetching ||
        !hasEnoughFunds
      }
      isProcessing={isProcessing}
      hasError={hasError}
      errorMessage={errorMessage}
      imageTagText={`Code for 1 ${productData.name}`}
      termsOfUseNote={(link) => (
        <>By purchasing, you accept Mezo&apos;s {link}</>
      )}
    >
      <MarketTag
        overrides={{
          Root: {
            style: {
              marginBottom: theme.sizing.scale700,
            },
          },
        }}
      >
        Earn bonus mats
      </MarketTag>

      <HeadingLarge>{productData.mainTitle}</HeadingLarge>

      <HeadingSmall marginBottom={theme.sizing.scale500}>
        {bigIntToCommaSeparatedFloat(
          BigInt(productDataDetails.data?.price ?? "0"),
          musdDecimals,
          musdDecimals,
        )}{" "}
        MUSD
      </HeadingSmall>

      <ParagraphLarge marginBottom={theme.sizing.scale600}>
        {productData.mainDescription}
      </ParagraphLarge>

      <ParagraphLarge marginBottom={theme.sizing.scale600}>
        Full product details available on{" "}
        <Link href={productData.productDetailLink} target="_blank">
          Ledger.com
        </Link>
        .
      </ParagraphLarge>

      <LabelLarge marginBottom={theme.sizing.scale600}>
        Redemption Details
      </LabelLarge>

      <ParagraphLarge marginBottom={theme.sizing.scale600}>
        You’ll receive a unique code to redeem on{" "}
        <Link href={links.LEDGER} target="_blank">
          Ledger.com
        </Link>
        . Order and shipping details will be completed directly with Ledger.
      </ParagraphLarge>
    </MarketItemDetailsLayoutWithActionButton>
  )
}
